'use client';

import {
	CommandDialog,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
} from '@/components/ui/command';
import { Provider } from '@/lib/providers/types';
import { getFundLink } from '@/lib/providers/utils';
import { Route } from '@/utils/constants';
import { ArrowTrendingUpIcon, PresentationChartLineIcon, SwatchIcon } from '@heroicons/react/24/outline';
import { HomeIcon, Search } from 'lucide-react';
import { useRouter } from 'next/navigation';
import React from 'react';
import { Input } from './ui/input';

type CommandPalletProps = {
	providers: Provider[];
};

export function DashboardCommandPalette({ providers }: CommandPalletProps) {
	const [open, setOpen] = React.useState(false);
	const router = useRouter();

	React.useEffect(() => {
		const down = (e: KeyboardEvent) => {
			if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
				e.preventDefault();
				setOpen((open) => !open);
			}
		};
		document.addEventListener('keydown', down);
		return () => document.removeEventListener('keydown', down);
	}, []);

	const runCommand = React.useCallback((command: () => unknown) => {
		setOpen(false);
		command();
	}, []);

	return (
		<>
			<CommandDialog open={open} onOpenChange={setOpen}>
				<CommandInput placeholder='Type a command or search...' />
				<CommandList>
					<CommandEmpty>No results found.</CommandEmpty>
					<CommandGroup heading='Tools'>
						<CommandItem onSelect={() => runCommand(() => router.push(Route.capaPerformance))}>
							<HomeIcon className='mr-2 h-4 w-4' />
							<span>Home</span>
						</CommandItem>
						<CommandItem onSelect={() => runCommand(() => router.push(Route.compare))}>
							<SwatchIcon className='mr-2 h-4 w-4' />
							<span>Compare</span>
						</CommandItem>
						<CommandItem onSelect={() => runCommand(() => router.push(Route.report))}>
							<PresentationChartLineIcon className='mr-2 h-4 w-4' />
							<span>Create report</span>
						</CommandItem>
					</CommandGroup>
					<CommandSeparator />
					<CommandGroup heading='Provider funds'>
						{providers.flatMap(provider => {
							return provider.funds.map((fund) => {
								return (
									<CommandItem
										key={fund.id}
										onSelect={() => runCommand(() => router.push(getFundLink(provider.slug, fund.id)))}
									>
										<ArrowTrendingUpIcon className='mr-2 h-4 w-4' />
										<span>{provider.name} - {fund.name}</span>
									</CommandItem>
								);
							});
						})}
					</CommandGroup>
				</CommandList>
			</CommandDialog>
			<form>
				<div className='relative flex-grow'>
					<Search className='absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground' />
					<Input
						type='search'
						placeholder='Search providers and tools...'
						className='w-full appearance-none bg-background pl-8 shadow-none'
						onSelect={(e) => {
							e.preventDefault();
							setOpen(true);
						}}
					/>
				</div>
			</form>
		</>
	);
}
